<template>
	<div v-if="list.length" class="ratio-section">
		<div class="ratio-title">
			<div class="ratio-heading">
				<span>{{ isDownPayment ? '首付' : '尾款' }}</span>
				<span v-if="showRange" class="amount-range">
					(范围{{ formatMoney(amountLimit.min_loan_amount) }}-{{
						formatMoney(amountLimit.max_loan_amount)
					}})
				</span>
			</div>
			<div v-if="list.length === 1" class="ratio-value">
				{{ displayRatio }}%
				<span>{{ formatMoney(amount) }}</span>
			</div>
			<div v-if="list.length > 1 && showInput" class="amount-input">
				<span>¥</span>
				<input
					v-model="amountInput"
					type="text"
					inputmode="numeric"
					@focus="focusAmount"
					@blur="changeAmount"
				/>
			</div>
		</div>
		<div v-if="showSlider" class="ratio-slider">
			<van-slider
				:value="sliderIndex"
				:min="0"
				:max="list.length - 1"
				:step="1"
				active-color="#040b29"
				inactive-color="#e6e7ea"
				@input="sliderIndex = $event"
				@change="changeRatio"
			>
				<template #button>
					<div class="slider-button">
						<span class="slider-value">{{ displayRatio }}%</span>
					</div>
				</template>
			</van-slider>
			<div class="ratio-range">
				<span>{{ parseInt(list[0] * 100) }}%</span>
				<span>{{ parseInt(list[list.length - 1] * 100) }}%</span>
			</div>
		</div>
	</div>
</template>

<script>
import { Slider } from 'vant'

export default {
	name: 'DownPaymentRatio',
	components: { VanSlider: Slider },
	props: {
		type: { type: String, default: 'tailRatio' },
		list: { type: Array, default: () => [] },
		// 首付/尾款的比例
		ratio: { type: [String, Number], default: '' },
		// 首付/尾款的金额
		amount: { type: [String, Number], default: '' },
		// 是否允许改变首付比例
		isRatioAlong: { type: Boolean, default: true },
		// 金额的限制范围{ min_loan_amount, max_loan_amount }
		amountLimit: { type: Object, default: () => ({}) }
	},
	data() {
		return { sliderIndex: 0, amountInput: this.formatAmount(this.amount), amountFocused: false }
	},
	computed: {
		// 是否为首付比例
		isDownPayment() {
			return this.type === 'downPaymentRatio'
		},
		displayRatio() {
			const index = Math.floor(this.sliderIndex)
			const start = parseInt(this.list[index] * 100)
			const end = parseInt(this.list[Math.min(index + 1, this.list.length - 1)] * 100)
			return Number((start + (end - start) * (this.sliderIndex - index)).toFixed(2))
		},
		showInput() {
			return this.isDownPayment && !this.isRatioAlong
		},
		showRange() {
			const { min_loan_amount, max_loan_amount } = this.amountLimit
			return (
				this.isDownPayment &&
				!this.isRatioAlong &&
				!!max_loan_amount &&
				!(max_loan_amount === min_loan_amount)
			)
		},
		showSlider() {
			return (
				this.list.length > 1 && Number(this.list[0]) !== Number(this.list[this.list.length - 1])
			)
		}
	},
	watch: {
		ratio: { immediate: true, handler: 'syncIndex' },
		list: { handler: 'syncIndex' },
		amount(value) {
			if (!this.amountFocused) this.amountInput = this.formatAmount(value)
		}
	},
	methods: {
		formatMoney(value) {
			return '¥' + Math.round(Number(value || 0)).toLocaleString('en-US')
		},
		formatAmount(value) {
			return value === '' || value === null || value === undefined
				? ''
				: Math.round(Number(value)).toLocaleString('en-US')
		},
		focusAmount() {
			this.amountFocused = true
			this.amountInput = String(this.amountInput).replace(/,/g, '')
		},
		// 将比例转换为滑块的位置
		syncIndex() {
			const value = Number(this.ratio)
			if (!this.list.length || !Number.isFinite(value) || value <= Number(this.list[0])) {
				this.sliderIndex = 0
				return
			}
			const upperIndex = this.list.findIndex(item => Number(item) >= value)
			if (upperIndex < 0) {
				this.sliderIndex = this.list.length - 1
				return
			}
			const lower = Number(this.list[upperIndex - 1])
			const upper = Number(this.list[upperIndex])
			this.sliderIndex = upperIndex - 1 + (value - lower) / (upper - lower)
		},
		changeRatio(index) {
			const value = this.list[index]
			if (value !== undefined) {
				this.sliderIndex = index
				this.$emit('ratio-change', value, index)
			}
		},
		changeAmount() {
			this.amountFocused = false
			const value = Number(this.amountInput)
			const min = Number(this.amountLimit.min_loan_amount)
			const max = Number(this.amountLimit.max_loan_amount)
			if (
				!/^\d+$/.test(String(this.amountInput)) ||
				!Number.isFinite(value) ||
				!Number.isFinite(min) ||
				!Number.isFinite(max) ||
				value < min ||
				value > max
			) {
				this.amountInput = this.formatAmount(this.amount)
				this.$toast('请输入正确的首付金额')
				return
			}
			this.amountInput = this.formatAmount(value)
			if (value !== Number(this.amount)) this.$emit('amount-change', value)
		}
	}
}
</script>

<style scoped lang="scss">
@import '~@nio-fe/nio-app-ui/es/style';

.ratio-section {
	margin-top: 40px;
	@include body_2(1.5, 400, false);
}

.ratio-title,
.ratio-range {
	display: flex;
	align-items: center;
	justify-content: space-between;

	> :first-child {
		margin-right: 10px;
	}
}

.ratio-title {
	align-items: flex-start;
	padding: 0 20px;
}

.ratio-heading {
	flex: 1;
	min-width: 0;
	@include subtitle_2(1.5, 500, false);
}

.ratio-value {
	text-align: right;

	span {
		margin-left: 8px;
	}
}

.ratio-slider {
	margin: 74px 0 0;
	padding: 0 30px;
}

.ratio-range {
	margin: 14px -10px 0;
	color: $text_tertiary;
	@include body_3(1.5, 400, false);
}

.amount-input {
	display: flex;
	align-items: center;
	width: 90px;
	max-width: 45%;
	flex-shrink: 0;
	padding: 6px 8px;
	box-sizing: border-box;
	border: 1px solid var(--border_divider_primary);

	> :first-child {
		margin-right: 4px;
	}

	&:focus-within {
		border-color: $text_primary;
	}

	input {
		width: 100%;
		min-width: 0;
		padding: 0;
		border: 0;
		text-align: left;
	}
}

.amount-range {
	display: block;
	margin-top: 2px;
	color: $text_tertiary;
	@include body_3(1.5, 400, false);
}

.slider-button {
	position: relative;
	width: 20px;
	height: 20px;
	border: 2px solid #040b29;
	border-radius: 50%;
	background: #ffffff;
	box-shadow: none;
}

.slider-value {
	position: absolute;
	bottom: 34px;
	left: 50%;
	transform: translateX(-50%);
	padding: 8px 10px;
	border-radius: 3px;
	background: var(--bg_inverse);
	color: #fff;
	white-space: nowrap;
	@include body_3(1.5, 400, false);

	&::after {
		position: absolute;
		bottom: -5px;
		left: 50%;
		width: 10px;
		height: 10px;
		background: var(--bg_inverse);
		content: '';
		transform: translateX(-50%) rotate(45deg);
	}

	&--start::after {
		left: 12px;
	}

	&--end::after {
		left: auto;
		right: 12px;
	}
}
</style>
